import React, { useState } from 'react'
import axios from 'axios'

import Style from './ThreatFeedDetections.module.css'
import ThreatFeedStatusMenu from './ThreatFeedStatusMenu'
import { formatThreatFeedDatetime } from '../../../utilities/datetime'
import { eventTypes } from '../../../utilities/eventTypes'

// ===========================================================================
// Threat Feed — View Details popup (PIXMC-6500).
//
// Mockup modal (Option A): left User/Threat details table, right Screenshot / HTML / Vision
// tabs. Driven straight off the clicked all-events row (which already carries the screenshot
// and page-content URLs), so opening it dispatches nothing and does not touch the shared
// events loading state. Presentational; ThreatFeed owns the open/close state.
// ===========================================================================

const STATUS_CLASS = {
  'PIXM Verified': Style.statusPixmVerified,
  'Verified Phish': Style.statusVerifiedPhish,
  'AI Suspicious': Style.statusAiSuspicious,
  'Reported Phish': Style.statusReportedPhish,
}

const userName = (e) => e.osUserId || e.osComputerName || e.osuuid || e.email || 'NA'

// The HTML blob URL is DERIVED from markedScreenshotURL for AI / PIXM / Google-Form /
// Unknown statuses (and reported-phish), not the raw pageContentURL — same rule as
// incidentDetails.getPageContentURL. Using pageContentURL directly is why no HTML loaded.
const AI_OR_UNKNOWN = ['AI Suspicious', 'Unknown', 'Google Form Password', 'PIXM Verified']
const htmlUrlOf = (e) => {
  if (!e) return null
  if (e.type === eventTypes.reported_phish && e.markedScreenshotURL) return `${e.markedScreenshotURL.slice(0, -5)}.html`
  if (AI_OR_UNKNOWN.includes(e.status) && e.markedScreenshotURL) return `${e.markedScreenshotURL.slice(0, -12)}.html`
  return e.pageContentURL || null
}

const ThreatFeedDetailModal = ({ event, onClose, onStatusAction }) => {
  const [tab, setTab] = useState('shot')
  const [html, setHtml] = useState(null)
  const [htmlError, setHtmlError] = useState(false)

  if (!event) return null

  const shot = event.resizedScreenshotURL || event.screenshotURL
  const marked = event.markedScreenshotURL

  const showTab = (next) => {
    setTab(next)
    if (next === 'html' && html === null && !htmlError) {
      const url = htmlUrlOf(event)
      if (!url) {
        setHtmlError(true)
        return
      }
      axios
        .get(url)
        .then((r) => setHtml(typeof r.data === 'string' ? r.data : JSON.stringify(r.data, null, 2)))
        .catch(() => setHtmlError(true))
    }
  }

  return (
    <div
      className={Style.modalOverlay}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className={Style.modal}>
        <div className={Style.modalHeader}>
          <div>
            <h2 className={Style.modalTitle}>Threat detail</h2>
            <p className={Style.modalSub}>
              {(event.brands || 'Phishing')} · detected {formatThreatFeedDatetime(event.detectionTime)}
            </p>
          </div>
          <ThreatFeedStatusMenu event={event} onAction={onStatusAction} />
        </div>

        <div className={Style.modalBody}>
          <div className={Style.detailGrid}>
            <div>
              <table className={Style.dTable}>
                <tbody>
                  <tr><td className={Style.dSection} colSpan={2}>User Details</td></tr>
                  <tr><td className={Style.dTableK}>User</td><td><b>{userName(event)}</b></td></tr>
                  <tr><td className={Style.dTableK}>Machine ID</td><td>{event.osuuid || 'NA'}</td></tr>
                  <tr><td className={Style.dTableK}>Computer</td><td>{event.osComputerName || 'NA'}</td></tr>
                  <tr><td className={Style.dTableK}>Opened</td><td>{formatThreatFeedDatetime(event.detectionTime)}</td></tr>
                  <tr><td className={Style.dSection} colSpan={2}>Threat Details</td></tr>
                  <tr><td className={Style.dTableK}>Brand</td><td><b>{event.brands || 'NA'}</b></td></tr>
                  <tr>
                    <td className={Style.dTableK}>Status</td>
                    <td><span className={STATUS_CLASS[event.status] || Style.statusDefault}>{event.status}</span></td>
                  </tr>
                  <tr><td className={Style.dTableK}>IP</td><td>{event.domainIPAddress || 'NA'}</td></tr>
                  <tr><td className={Style.dTableK}>URL</td><td style={{ wordBreak: 'break-all' }}>{event.url}</td></tr>
                </tbody>
              </table>
            </div>

            <div>
              <div className={Style.dTabs}>
                <button className={`${Style.dTab} ${tab === 'shot' ? Style.dTabActive : ''}`} onClick={() => showTab('shot')}>Screenshot</button>
                <button className={`${Style.dTab} ${tab === 'html' ? Style.dTabActive : ''}`} onClick={() => showTab('html')}>HTML</button>
                <button className={`${Style.dTab} ${tab === 'vision' ? Style.dTabActive : ''}`} onClick={() => showTab('vision')}>Vision Analysis</button>
              </div>

              {tab === 'shot' && (shot
                ? <img className={Style.dShotImg} src={shot} alt="Detection screenshot" />
                : <div className={Style.dShot}>No screenshot</div>)}

              {tab === 'html' && (htmlError
                ? <div className={Style.dShot}>HTML not available</div>
                : html === null
                  ? <div className={Style.dShot}>Loading…</div>
                  : <pre className={Style.dHtml}>{html}</pre>)}

              {tab === 'vision' && (marked
                ? <img className={Style.dShotImg} src={marked} alt="Vision analysis" />
                : <div className={Style.dShot}>No vision analysis</div>)}
            </div>
          </div>
        </div>

        <div className={Style.modalFooter}>
          <button className={Style.btn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export default ThreatFeedDetailModal
